import { Injectable, signal, inject } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, map } from 'rxjs';
import {
  ContextService,
  GlobalStore,
} from '@maximizer/core/shared/data-access';
import {
  OutlookStore,
  TrackerService,
  PageTitleService,
} from '@maximizer/outlook/shared/data-access';
import { EntryType, PageTitle } from '@maximizer/outlook/shared/domain';
import { UserRights } from '@maximizer/core/shared/domain';

@Injectable()
export class NavigationService {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly contextService = inject(ContextService);
  private readonly outlookStore = inject(OutlookStore);
  private readonly globalStore = inject(GlobalStore);
  private readonly tracker = inject(TrackerService);
  private readonly pageTitleService = inject(PageTitleService);

  private readonly maxHistorySize = 10;
  private readonly defaultLink =
    this.contextService.website || 'https://www.maximizer.com';
  private readonly noBackRoutes = [
    'MailCompose',
    'MailRead',
    'CalendarCompose',
    'CalendarRead',
  ];
  private readonly unauthorizedRoutes = [
    '/login',
    '/login-email',
    '/authorize',
    // Browser-only route — backup to service-level MSAL/refresh suppression.
    '/sync-configuration',
  ];

  private readonly _pageTitle = signal<string>('');
  private readonly _canNavigateBack = signal<boolean>(false);
  private readonly _isAuthorizedRoute = signal<boolean>(false);
  private readonly _deeplink = signal<string>(this.defaultLink);
  private readonly _isNavigatingBack = signal<boolean>(false);
  private readonly _currentUrl = signal<string>(this.router.url);
  backNavigationOverride = signal<{
    state: boolean;
    pathSegments: string[];
    onBack?: () => void;
  }>({
    state: false,
    pathSegments: [],
  });

  get canNavigateBack(): boolean {
    return this._canNavigateBack();
  }
  get isAuthorizedRoute(): boolean {
    return this._isAuthorizedRoute();
  }
  get showMenu(): boolean {
    return this._isAuthorizedRoute();
  }

  deeplink = this._deeplink;
  pageTitle = this._pageTitle;
  currentUrl = this._currentUrl.asReadonly();
  pageHistory: ActivatedRoute[] = [];

  constructor() {
    this.setupRouteTracking();

    const initialRoute = this.getDeepestActivatedRoute(this.activatedRoute);
    this.storeRouteInHistory(initialRoute);
  }

  private setupRouteTracking(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.getDeepestActivatedRoute(this.activatedRoute)),
      )
      .subscribe((route) => this.handleRouteChange(route));
  }

  private handleRouteChange(route: ActivatedRoute): void {
    this._currentUrl.set(this.router.url);

    const title = route.snapshot.routeConfig?.title as PageTitle | undefined;
    if (!title) return;

    let userRights: UserRights | undefined = undefined;
    if (this.globalStore.session()) {
      userRights = this.globalStore.session()?.user.rights;
    }

    this._pageTitle.set(
      this.pageTitleService.mapPageTitle(title, route, userRights),
    );

    this.tracker.trackPageView(title);
    this._canNavigateBack.set(!this.noBackRoutes.includes(title));
    this.updateMenuVisibility();
    this.storeRouteInHistory(route);

    if (title === PageTitle.EntryPage && this.outlookStore.isCloud()) {
      this.populateDeeplink(route);
    } else {
      this._deeplink.set(this.defaultLink);
    }
  }

  private populateDeeplink(route: ActivatedRoute): void {
    const routeSnapshot = route.snapshot;
    const type = routeSnapshot.paramMap.get('type');
    const id = routeSnapshot.paramMap.get('id');

    const accountLink = this.buildAccountLink();
    if (!accountLink) {
      this._deeplink.set(this.defaultLink);
      return;
    }

    if (type && id) {
      if (type === EntryType.AbEntry) {
        this._deeplink.set(`${accountLink}?ss=KEY(${id})`);
      } else if (type === EntryType.Lead) {
        this._deeplink.set(`${accountLink}?sslead=KEY(${id})`);
      } else {
        this._deeplink.set(this.defaultLink);
      }
    } else {
      this._deeplink.set(this.defaultLink);
    }
  }

  private buildAccountLink(): string | null {
    const urlArray = this.contextService.website.split('/');
    const domainIndex = urlArray.findIndex((segment) =>
      segment.toLowerCase().includes('maximizer'),
    );

    if (domainIndex === -1) return null;

    // Build clean domain (e.g. https://staging1.maximizercrmlive.com/)
    let domain = '';
    for (let i = 0; i <= domainIndex; i++) {
      domain += urlArray[i] + '/';
    }

    // Convert staging1 -> stagingw and add alias
    return domain.replace('1', 'w') + this.contextService.alias;
  }

  private updateMenuVisibility(): void {
    const authorized = !this.isUnauthorizedRoute();
    this._isAuthorizedRoute.set(authorized);
  }

  private isUnauthorizedRoute(): boolean {
    const url = this.router.url;
    return this.unauthorizedRoutes.some(
      (route) =>
        url === route ||
        url.startsWith(`${route}/`) ||
        url.startsWith(`${route}?`),
    );
  }

  private getDeepestActivatedRoute(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  // Custom breadcrumb navigation - Office.js breaks location API in Outlook popups
  storeRouteInHistory(route: ActivatedRoute): void {
    if (!route) return;

    if (this._isNavigatingBack()) {
      this._isNavigatingBack.set(false);
      return;
    }

    const newUrl = this.buildUrlFromRoute(route);
    const newQueryParams = route.snapshot.queryParams;

    const existingIndex = this.pageHistory.findIndex((route) => {
      const existingUrl = this.buildUrlFromRoute(route);
      return (
        existingUrl === newUrl &&
        JSON.stringify(route.snapshot.queryParams) ===
          JSON.stringify(newQueryParams)
      );
    });

    if (existingIndex >= 0) {
      this.pageHistory = this.pageHistory.slice(existingIndex);
    } else {
      this.pageHistory.unshift(route);
    }

    if (this.pageHistory.length > this.maxHistorySize) {
      this.pageHistory = this.pageHistory.slice(0, this.maxHistorySize);
    }
  }

  private buildUrlFromRoute(route: ActivatedRoute): string {
    return route.snapshot.url.map((segment) => segment.path).join('/');
  }

  navigateBack(): void {
    if (this.backNavigationOverride().state) {
      const { pathSegments, onBack } = this.backNavigationOverride();
      this.backNavigationOverride.set({ state: false, pathSegments: [] });

      if (onBack) {
        onBack();
        return;
      }

      this.router.navigate(pathSegments);
    } else if (this.pageHistory.length > 1) {
      const lastPage = this.pageHistory[1];
      const snapshot = lastPage.snapshot;

      this.removeCurrentPageFromHistory();
      this._isNavigatingBack.set(true);

      const url = this.buildUrlFromRoute(lastPage);
      this.router.navigate([url], {
        queryParams: snapshot.queryParams,
        fragment: snapshot.fragment ?? undefined,
      });
    } else {
      this.router.navigate(['/home']);
    }
  }

  private removeCurrentPageFromHistory(): void {
    if (this.pageHistory.length > 0) {
      this.pageHistory.shift();
    }
  }
}
